/**
 * weblabColumns — column model for the Weblab Tracker grid.
 *
 * Ported from the standalone tracker's WeblabGridComponent column defs,
 * keeping the read-only fields and dropping the editable / action / watchlist
 * columns (those are write paths that stay in the standalone tracker).
 *
 * Field names match the Athena table `weblab_metrics_data_prod` (see the
 * export DAG's Glue schema in export_weblab_metrics.py).
 *
 * FORMATTING MATCHES THE TRACKER GRID EXACTLY (WeblabGridComponent col defs):
 *   - prob   : "raw"      — tracker uses cellDataType number, NO formatter.
 *              Value shown as-is (NOT multiplied by 100, no "%").
 *   - lift   : "raw"      — header says "lift(%)" but the CELL value is the
 *              raw number with no formatter and no "%" appended.
 *   - impact : "currency" — currencyFormatter, EXCEPT SP Clicks / Paid Units
 *              which use "number" (numberFormatter).
 *   - Expected Revenue Impact & other $ fields: "currency".
 *
 * IMPORTANT: an earlier version used a "percent" format that multiplied
 * lift/prob by 100 and appended "%". That did NOT match the tracker and has
 * been removed.
 */

import { currencyFormatter, numberFormatter } from "./weblabFormatters";

/** A single weblab row as returned by Athena (all values arrive as strings). */
export interface WeblabRow {
  id?: string;
  weblab_id?: string;
  status?: string;
  marketplace_id?: string;
  owners?: string;
  bar_raiser_wiki?: string;
  short_description?: string;
  deep_dive_link?: string;
  global_init_name?: string;
  experiment_type?: string;
  smoke_test?: string;
  baseline_treatment?: string;
  treatment?: string;
  experiment_start_date?: string;
  analysis_duration?: string;
  page_type?: string;
  device?: string;
  expected_launch_date?: string;
  actual_launch_date?: string;
  analysis_start_date?: string;
  analysis_end_date?: string;
  team_name?: string;
  sub_team?: string;
  weblab_type?: string;
  status_report?: string;
  select_for_default_view?: string;
  run_date?: string;
  creator?: string;
  last_updated_by?: string;
  last_updated_time?: string;
  is_gating_weblab?: string;
  gating_weblab?: string;
  shared_flag?: string;
  rev_share?: string;
  is_manual_weblab?: string;
  irrelevance_rate_control?: string;
  irrelevance_rate_treatment?: string;
  revenue_impact_planned_weblab?: string;
  job_ids?: string;
  pirrel_human_relevance_audit?: string;
  ctr?: string;
  coverage?: string;
  cpc?: string;
  acos?: string;
  // Metric triplets (probability / lift / annualized impact)
  sp_revenue_prob?: string;
  sp_revenue_lift?: string;
  sp_revenue_impact?: string;
  ops_prob?: string;
  ops_lift?: string;
  ops_impact?: string;
  gccp_prob?: string;
  gccp_lift?: string;
  gccp_impact?: string;
  net_cp_prob?: string;
  net_cp_lift?: string;
  net_cp_impact?: string;
  sp_clicks_prob?: string;
  sp_clicks_lift?: string;
  sp_clicks_impact?: string;
  paid_units_prob?: string;
  paid_units_lift?: string;
  paid_units_impact?: string;
  csales_prob?: string;
  csales_lift?: string;
  csales_impact?: string;
  sb_revenue_prob?: string;
  sb_revenue_lift?: string;
  sb_revenue_impact?: string;
  sb_clicks_prob?: string;
  sb_clicks_lift?: string;
  sb_clicks_impact?: string;
  sp_attributed_ops_1day_prob?: string;
  sp_attributed_ops_1day_lift?: string;
  sp_attributed_ops_1day?: string;
  sp_roas_1day_prob?: string;
  sp_roas_1day_lift?: string;
  sp_roas_1day?: string;
  [key: string]: any;
}

/**
 * Cell render format:
 *   - "text"     : as-is
 *   - "raw"      : as-is (used for prob/lift — number shown unformatted)
 *   - "number"   : numberFormatter (thousands separators, 0 decimals)
 *   - "currency" : currencyFormatter (USD, 0 decimals)
 */
export type WeblabColumnFormat = "text" | "raw" | "number" | "currency";

export interface WeblabColumn {
  /** Athena field name. */
  field: keyof WeblabRow | string;
  /** Column header shown in the grid. */
  header: string;
  /** How to render the cell value. */
  format?: WeblabColumnFormat;
  /** Whether the column is shown by default (others available via column menu). */
  defaultVisible?: boolean;
  /** Grouping label for the column chooser. */
  group?: "Identity" | "Config" | "Dates" | "Metrics";
}

export const WEBLAB_COLUMNS: WeblabColumn[] = [
  // Identity
  {
    field: "weblab_id",
    header: "Weblab ID",
    group: "Identity",
    defaultVisible: true,
  },
  {
    field: "team_name",
    header: "Team",
    group: "Identity",
    defaultVisible: true,
  },
  {
    field: "sub_team",
    header: "Sub Team",
    group: "Identity",
    defaultVisible: true,
  },
  {
    field: "owners",
    header: "Owners",
    group: "Identity",
    defaultVisible: true,
  },
  {
    field: "short_description",
    header: "Description",
    group: "Identity",
    defaultVisible: true,
  },
  {
    field: "bar_raiser_wiki",
    header: "Bar Raiser Wiki",
    group: "Identity",
    defaultVisible: false,
  },
  {
    field: "global_init_name",
    header: "Global Initiative",
    group: "Identity",
    defaultVisible: false,
  },
  {
    field: "creator",
    header: "Creator",
    group: "Identity",
    defaultVisible: false,
  },
  {
    field: "last_updated_by",
    header: "Last Updated By",
    group: "Identity",
    defaultVisible: false,
  },

  // Config
  {
    field: "experiment_type",
    header: "Experiment Type",
    group: "Config",
    defaultVisible: true,
  },
  {
    field: "marketplace_id",
    header: "Marketplace",
    group: "Config",
    defaultVisible: true,
  },
  { field: "device", header: "Device", group: "Config", defaultVisible: false },
  {
    field: "page_type",
    header: "Page Type",
    group: "Config",
    defaultVisible: false,
  },
  {
    field: "baseline_treatment",
    header: "Baseline",
    group: "Config",
    defaultVisible: true,
  },
  {
    field: "treatment",
    header: "Treatment",
    group: "Config",
    defaultVisible: true,
  },
  {
    field: "smoke_test",
    header: "Smoke Test",
    group: "Config",
    defaultVisible: false,
  },
  {
    field: "analysis_duration",
    header: "Analysis Duration",
    format: "number",
    group: "Config",
    defaultVisible: false,
  },
  {
    field: "rev_share",
    header: "Revenue Share(%)",
    format: "raw",
    group: "Config",
    defaultVisible: false,
  },
  {
    field: "revenue_impact_planned_weblab",
    header: "Expected Revenue Impact",
    format: "currency",
    group: "Config",
    defaultVisible: false,
  },

  // Dates
  {
    field: "experiment_start_date",
    header: "Start Date",
    group: "Dates",
    defaultVisible: true,
  },
  {
    field: "expected_launch_date",
    header: "Expected Launch",
    group: "Dates",
    defaultVisible: false,
  },
  {
    field: "actual_launch_date",
    header: "Actual Launch",
    group: "Dates",
    defaultVisible: false,
  },
  {
    field: "analysis_start_date",
    header: "Analysis Start",
    group: "Dates",
    defaultVisible: false,
  },
  {
    field: "analysis_end_date",
    header: "Analysis End",
    group: "Dates",
    defaultVisible: false,
  },

  // Metrics — prob & lift are raw; impact is currency (count metrics use number)
  {
    field: "sp_revenue_lift",
    header: "SP Revenue: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_revenue_prob",
    header: "SP Revenue: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_revenue_impact",
    header: "SP Revenue: impact",
    format: "currency",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "ops_prob",
    header: "OPS: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "ops_lift",
    header: "OPS: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "ops_impact",
    header: "OPS: impact",
    format: "currency",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "gccp_prob",
    header: "GCCP: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "gccp_lift",
    header: "GCCP: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "gccp_impact",
    header: "GCCP: impact",
    format: "currency",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "net_cp_prob",
    header: "Net CP: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "net_cp_lift",
    header: "Net CP: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "net_cp_impact",
    header: "Net CP: impact",
    format: "currency",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_clicks_prob",
    header: "SP Clicks: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "sp_clicks_lift",
    header: "SP Clicks: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "sp_clicks_impact",
    header: "SP Clicks: impact",
    format: "number",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "paid_units_prob",
    header: "Paid Units: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "paid_units_lift",
    header: "Paid Units: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "paid_units_impact",
    header: "Paid Units: impact",
    format: "number",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "csales_prob",
    header: "CSales: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "csales_lift",
    header: "CSales: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "csales_impact",
    header: "CSales: impact",
    format: "currency",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sb_revenue_prob",
    header: "SB Revenue: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "sb_revenue_lift",
    header: "SB Revenue: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: false,
  },
  {
    field: "sb_revenue_impact",
    header: "SB Revenue: impact",
    format: "currency",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sb_clicks_prob",
    header: "SB Clicks: probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sb_clicks_lift",
    header: "SB Clicks: lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sb_clicks_impact",
    header: "SB Clicks: impact",
    format: "number",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_attributed_ops_1day_prob",
    header: "SP Attr. OPS (1-day): probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_attributed_ops_1day_lift",
    header: "SP Attr. OPS (1-day): lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_attributed_ops_1day",
    header: "SP Attr. OPS (1-day): impact",
    format: "currency",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_roas_1day_prob",
    header: "SP ROAS (1-day): probability",
    format: "raw",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_roas_1day_lift",
    header: "SP ROAS (1-day): lift(%)",
    format: "raw",
    group: "Metrics",
    defaultVisible: true,
  },
  {
    field: "sp_roas_1day",
    header: "SP ROAS (1-day): impact",
    format: "number",
    group: "Metrics",
    defaultVisible: true,
  },
];

/**
 * Format a raw Athena string value for grid display, matching the tracker.
 */
export function formatWeblabValue(
  value: any,
  format: WeblabColumnFormat = "text"
): string {
  if (value === null || value === undefined || value === "") return "—";
  switch (format) {
    case "currency": {
      const s = currencyFormatter(value);
      return s === "" ? String(value) : s;
    }
    case "number": {
      const s = numberFormatter(value);
      return s === "" ? String(value) : s;
    }
    case "raw":
    case "text":
    default:
      return String(value);
  }
}
